import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Logo from '../components/Logo';
import AnnouncementBar from '../components/AnnouncementBar';
import { useAuth } from '../context/AuthContext';
import '../styles/landing-story.css';

/**
 * Landing + authentication.
 *
 * A faithful port of the original index.html: the same `auth-shell` split into
 * an intro on the left and a three-state auth panel on the right (sign in,
 * create account, reset password). The class names and copy are the originals,
 * so the design system styles it exactly as it did before.
 *
 * Below the auth shell the page tells the full story: the problem, the
 * parties, the record, the money and the way in. The story sections are styled
 * as a continuation of the intro column (same fonts, navy, hairlines, grid
 * texture), so the page reads as one piece. Written with the presentation
 * deck and the business proposal as the source.
 *
 * The blue mark is used here because this page is light throughout. The white
 * variant belongs on the navy sidebar.
 */

const YEAR = new Date().getFullYear();

const FOOTER = (
  <footer className="auth-footer">
    <p>
      &copy; {YEAR} Valdon Building &amp; Construction Services · Version 1.2
    </p>
  </footer>
);

/**
 * Natural Earth 110m landmass, rasterised to a 2 degree grid: 180 columns
 * (longitude) x 90 rows (latitude), one bit per cell, 3,279 land cells in
 * 2,025 bytes. High-latitude rows are thinned by cosine-latitude so the dot
 * density stays even on the sphere. Row 0 starts at latitude -90 so bit
 * index = row * 180 + col. Embedded as base64 so the landing page stays
 * free of runtime fetches.
 */
const LAND_GRID = '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';
export default function Landing() {
  const { signIn, signUp, sendPasswordReset } = useAuth();
  const navigate = useNavigate();

  const [state, setState] = useState('signin'); // signin | register | forgot
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState({ text: '', type: '' });
  const [scrollTick, setScrollTick] = useState(0);
  const [openFaq, setOpenFaq] = useState(0);
  const panelRef = useRef(null);

  function toggleFaq(i) {
    setOpenFaq((cur) => (cur === i ? -1 : i));
  }

  /**
   * Switch the panel, and make sure the panel is on screen.
   *
   * Above 1024px the intro and the panel sit side by side, so there is
   * nothing to scroll. Below it they stack — the form sits under the hero —
   * so choosing "Sign In" or "Get Started" has to bring the form into view,
   * otherwise the click looks like it did nothing.
   */
  function go(next) {
    setState(next);
    setMsg({ text: '', type: '' });
    setScrollTick((n) => n + 1);
  }

  useEffect(() => {
    if (!scrollTick) return; // the first paint is not a choice the user made
    const panel = panelRef.current;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    const offScreen = rect.top < 0 || rect.top > window.innerHeight * 0.3;
    if (!offScreen) return;
    scrollToPosition(Math.max(0, rect.top + window.scrollY - 24));
  }, [state, scrollTick]);

  // Inside the mock the data arrives the way live data would: the greeting
  // types itself in, the figures spin up like a pump meter, and the
  // progress bar rises to its percentage. Everything else on the dashboard
  // is already there when it scrolls into view; reduced motion skips it.
  useEffect(() => {
    const mock = document.querySelector('.vm-appwin');
    if (!mock) return;
    if (window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const greet = mock.querySelector('[data-typed]');
    const greetText = greet ? greet.getAttribute('data-typed') : '';

    // Meter motion, the way CountUp.js and odometer.js do it: easeOutExpo
    // sweeps most of the range in the first third of the duration and
    // settles asymptotically, so the digits blur past at the start and the
    // last tick lands slowly. Output is integer-quantised on
    // requestAnimationFrame, and a wheel is only written to when the
    // displayed digit changes.
    const easeOutExpo = (t) => (t >= 1 ? 1 : 1 - Math.pow(2, -10 * t));
    const TYPE_MS = 850;
    const METER_MS = 1150;

    const figures = [...mock.querySelectorAll('[data-count]')].map((el, i) => ({
      el,
      to: parseInt(el.getAttribute('data-count'), 10) || 0,
      // Wheels do not all lock at the same instant (odometer-style): each
      // one spins a touch longer than the last. The first wheel is the
      // printed percentage and it stays synced with the bar.
      dur: METER_MS + i * 260,
    }));
    const bar = mock.querySelector('.vm-appprog-bar > span');
    const barTo = bar ? parseInt(bar.getAttribute('data-target'), 10) || 0 : 0;

    // Park the live pieces at their start values. The mock sits below the
    // fold at load, so nothing on screen changes yet.
    if (greet) greet.textContent = '';
    figures.forEach(({ el }) => { el.textContent = '0'; });
    if (bar) bar.style.width = '0%';

    const maxDur = METER_MS + (figures.length - 1) * 260;
    let frameId = 0;

    const run = () => {
      const t0 = performance.now();
      let typedChars = 0;
      const tick = (now) => {
        const t = now - t0;
        if (greet) {
          const chars = Math.round(Math.min(1, t / TYPE_MS) * greetText.length);
          if (chars !== typedChars) {
            typedChars = chars;
            greet.textContent = greetText.slice(0, chars);
          }
        }
        figures.forEach(({ el, to, dur }) => {
          let shown;
          if (to <= 2) {
            // A tiny count is a discrete event, not a spin: the stage
            // counter sits at zero and locks in during the final stretch,
            // exactly like the last wheel on a meter.
            shown = t / dur >= 0.72 ? to : 0;
          } else {
            shown = Math.round(to * easeOutExpo(Math.min(1, t / dur)));
          }
          const text = String(shown);
          if (el.textContent !== text) el.textContent = text;
        });
        if (bar) {
          bar.style.width = (barTo * easeOutExpo(Math.min(1, t / METER_MS))) + '%';
        }
        if (t < maxDur) {
          frameId = requestAnimationFrame(tick);
        } else {
          if (greet) greet.textContent = greetText;
          figures.forEach(({ el, to }) => { el.textContent = String(to); });
          if (bar) bar.style.width = barTo + '%';
        }
      };
      frameId = requestAnimationFrame(tick);
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          observer.unobserve(entry.target);
          run();
        }
      });
      // Any pixel of the mock reaching the top 80% of the viewport starts
      // the sequence. A plain ratio threshold would never fire on phones,
      // where a tall stacked mock can only ever be a fraction visible.
    }, { threshold: 0, rootMargin: '0px 0px -20% 0px' });
    observer.observe(mock);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frameId);
    };
  }, []);

  // ── the globe ──────────────────────────────────────────────────────────────
  // A hand-drawn orthographic globe on a canvas: Kenya front and centre at
  // first paint, client cities around the world, and one verified line from
  // each to the site. It then rotates slowly; Kenya returns every cycle.
  useEffect(() => {
    const canvas = document.getElementById('globeCanvas');
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let width = 0;
    let height = 0;
    let radius = 0;
    let dpr = 1;
    let angle = 0;
    let frameId = 0;
    let landVecs = [];

    const deg = Math.PI / 180;
    const NAIROBI = { lat: -1.29, lng: 36.82 };

    // Longitude 0 faces the viewer at angle 0; north is up.
    function xyz(p, r = 1) {
      const cl = Math.cos(p.lat * deg);
      const sl = Math.sin(p.lat * deg);
      const co = Math.cos(p.lng * deg);
      const so = Math.sin(p.lng * deg);
      return { x: r * cl * so, y: r * sl, z: r * cl * co };
    }

    function rotatePoint(p, a) {
      const ca = Math.cos(a);
      const sa = Math.sin(a);
      return {
        x: p.x * ca + p.z * sa,
        y: p.y,
        z: -p.x * sa + p.z * ca,
      };
    }

    // Land dot vectors from the embedded Natural Earth raster: two degrees
    // per cell, thinned by cosine-latitude so the dot density stays even on
    // the sphere.
    function buildLand() {
      landVecs = [];
      const raw = window.atob(LAND_GRID);
      for (let i = 0; i < raw.length; i++) {
        const byte = raw.charCodeAt(i);
        if (!byte) continue;
        for (let k = 0; k < 8; k++) {
          if ((byte & (1 << k)) === 0) continue;
          const idx = i * 8 + k;
          const lat = -90 + Math.floor(idx / 180) * 2 + 1;
          const lng = -180 + (idx % 180) * 2 + 1;
          landVecs.push({ v: xyz({ lat, lng }, 1) });
        }
      }
    }

    function resize() {
      dpr = window.devicePixelRatio || 1;
      const rect = canvas.parentElement.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = width + 'px';
      canvas.style.height = height + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      radius = Math.min(width, height) * 0.45;
    }

    function draw(_now) {
      const cx = width / 2;
      const cy = height / 2;

      ctx.clearRect(0, 0, width, height);

      // Sphere: one flat paper tone and a hairline rim. No shading tricks:
      // the shape and the dot texture carry the picture.
      ctx.fillStyle = '#f3f6fb';
      ctx.beginPath();
      ctx.arc(cx, cy, radius, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = 'rgba(122, 138, 170, 0.5)';
      ctx.lineWidth = 1;
      ctx.stroke();

      // Continents as an open dot matrix. One tone, one size: a clear,
      // even texture across the sphere.
      const dots = new Path2D();
      for (const cell of landVecs) {
        const q = rotatePoint(cell.v, angle);
        if (q.z <= 0) continue;
        const px = cx + q.x * radius;
        const py = cy - q.y * radius;
        dots.rect(px - 1, py - 1, 2, 2);
      }
      ctx.fillStyle = 'rgba(30, 58, 138, 0.5)';
      ctx.fill(dots);

      // The globe draws nothing else: no pins, no labels, no borders.
      // The world slowly turning is the whole picture.
    }

    function animate() {
      const now = performance.now();
      // A slow, continuous rotation. The front longitude travels east, so
      // the dot-matrix world turns against the flat sphere.
      angle -= 0.0009;
      draw(now);
      frameId = requestAnimationFrame(animate);
    }
    const reduced = window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    resize();
    buildLand();

    // First paint faces Kenya, so East Africa is what the visitor sees
    // first. The globe then rotates slowly, a plain turning world.
    angle = -NAIROBI.lng * deg;

    if (reduced) {
      // Reduced motion: one honest painted frame, no loop.
      draw(0);
    } else {
      animate();
    }

    const onResize = () => {
      resize();
      draw(performance.now());
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      if (frameId) cancelAnimationFrame(frameId);
    };
  }, []);

  // ── sign in ──────────────────────────────────────────────────────────────
  async function onSignIn(e) {
    e.preventDefault();
    setBusy(true);
    setMsg({ text: '', type: '' });
    try {
      await signIn(e.target.email.value, e.target.password.value);
      navigate('/', { replace: true });
    } catch (err) {
      setMsg({ text: err.message || 'Unable to sign in. Please try again.', type: 'error' });
    } finally {
      setBusy(false);
    }
  }

  // ── create account ───────────────────────────────────────────────────────
  async function onRegister(e) {
    e.preventDefault();
    const f = e.target;
    const fullName = f.full_name.value.trim();
    const email = f.email.value.trim();
    const phone = f.phone.value.trim();
    const password = f.password.value;
    const confirm = f.confirm_password.value;
    const role = f.role.value;

    // The original validation, unchanged.
    if (!fullName || !email || !password || !confirm) {
      return setMsg({ text: 'Please fill in all required fields.', type: 'error' });
    }
    if (password.length < 6) {
      return setMsg({ text: 'Password must be at least 6 characters long.', type: 'error' });
    }
    if (!/[A-Z]/.test(password)) {
      return setMsg({ text: 'Password must contain at least one uppercase letter.', type: 'error' });
    }
    if (!/[a-z]/.test(password)) {
      return setMsg({ text: 'Password must contain at least one lowercase letter.', type: 'error' });
    }
    if (!/[0-9]/.test(password)) {
      return setMsg({ text: 'Password must contain at least one number.', type: 'error' });
    }
    if (!/[^A-Za-z0-9]/.test(password)) {
      return setMsg({ text: 'Password must contain at least one special character.', type: 'error' });
    }
    if (password !== confirm) {
      return setMsg({ text: 'Passwords do not match.', type: 'error' });
    }
    if (!role) {
      return setMsg({
        text: 'Please select whether you are a Client, Site Agent, or Project Manager.',
        type: 'error',
      });
    }
    if (role === 'administrator') {
      return setMsg({
        text: 'Administrator accounts cannot be created through public registration.',
        type: 'error',
      });
    }

    setBusy(true);
    setMsg({ text: '', type: '' });
    try {
      await signUp({ fullName, email, password, phone, role });
      setMsg({
        text:
          role === 'project_manager'
            ? 'Your Project Manager account has been created and is waiting for Valdon to approve it. You can sign in right away, but the workspace unlocks once approved.'
            : role === 'site_agent'
              ? 'Your Site Agent account has been created and is waiting for Valdon to approve it. You can sign in right away, but the workspace unlocks once approved.'
              : 'Your client account is ready. Sign in and create your first project.',
        type: 'success',
      });
    } catch (err) {
      const text = err.message?.includes('already registered')
        ? 'An account with this email already exists. Please sign in instead.'
        : 'Unable to create your account right now. Please try again later.';
      setMsg({ text, type: 'error' });
    } finally {
      setBusy(false);
    }
  }

  // ── forgot password ──────────────────────────────────────────────────────
  async function onForgot(e) {
    e.preventDefault();
    setBusy(true);
    setMsg({ text: '', type: '' });
    try {
      await sendPasswordReset(e.target.email.value);
      setMsg({ text: 'If that email exists, a reset link is on its way.', type: 'success' });
    } catch {
      setMsg({ text: 'Unable to send the reset link. Please try again.', type: 'error' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="landing-page">
      <AnnouncementBar />
      <main className="auth-shell">
        {/* ── intro ─────────────────────────────────────────────────────── */}
        <div className="auth-intro">
          <nav className="auth-intro-nav">
            <a className="auth-intro-logo" href="#signin" onClick={(e) => { e.preventDefault(); go('signin'); }}>
              <Logo variant="blue" className="auth-intro-logo-img" height={40} />
              <div>
                <div className="auth-intro-logo-text">VALDON</div>
                <div className="auth-intro-logo-sub">Site Control 360</div>
              </div>
            </a>
            <div className="auth-intro-nav-links">
              <a href="#dashboard" onClick={(e) => { e.preventDefault(); scrollToSection('dashboard'); }}>The Platform</a>
              <a href="#signin" onClick={(e) => { e.preventDefault(); go('signin'); }}>Sign In</a>
              <a
                className="auth-intro-nav-cta"
                href="#get-started"
                onClick={(e) => { e.preventDefault(); go('register'); }}
              >
                GET STARTED
              </a>
            </div>
          </nav>

          <div className="auth-intro-body">
            <h1 className="auth-intro-headline">
              Valdon Site Control 360
              <br />
              Construction transparency
              <br />
              for every project.
            </h1>
            <p className="auth-intro-text">
              Valdon Site Control 360 gives clients verified records of what is happening
              on their site: progress, work done, labour, materials and photos, all in one place.
            </p>
            <div className="auth-intro-actions">
              <a
                className="auth-intro-btn-primary"
                href="#get-started"
                onClick={(e) => { e.preventDefault(); go('register'); }}
              >
                GET STARTED
              </a>
            </div>
            <p className="auth-intro-note">Built for clients, project managers and site teams.</p>
          </div>
        </div>

        {/* ── auth panel ────────────────────────────────────────────────── */}
        <div className="auth-panel-wrap" ref={panelRef}>
          <div className="auth-panel">
            {/* Sign In */}
            <div className={`auth-state${state === 'signin' ? ' is-active' : ''}`}>
              <header className="auth-header">
                <h2 className="auth-title">Sign in to Site Control 360</h2>
                <p className="auth-subtitle">Enter your credentials to access your projects.</p>
              </header>

              {msg.text && state === 'signin' && (
                <div className={`message ${msg.type}`}>
                  <p className="message-text" role="alert">{msg.text}</p>
                </div>
              )}

              <form className="auth-form" onSubmit={onSignIn} noValidate>
                <div className="form-field">
                  <label htmlFor="login-email">Email</label>
                  <input type="email" id="login-email" name="email" autoComplete="email" required placeholder="you@company.com" />
                </div>
                <div className="form-field">
                  <label htmlFor="login-password">Password</label>
                  <input type="password" id="login-password" name="password" autoComplete="current-password" required placeholder="Enter your password" />
                </div>
                <button type="submit" id="login-button" className="btn btn-primary btn-block" disabled={busy}>
                  SIGN IN
                </button>
              </form>

              <div className="auth-divider"><span>New to Valdon?</span></div>
              <button type="button" className="btn btn-secondary btn-block" onClick={() => go('register')}>
                Create an account
              </button>
              {FOOTER}
            </div>

            {/* Create Account */}
            <div className={`auth-state${state === 'register' ? ' is-active' : ''}`}>
              <header className="auth-header">
                <h2 className="auth-title">Create your account</h2>
                <p className="auth-subtitle">Register to get started with Valdon Site Control 360.</p>
              </header>

              {msg.text && state === 'register' && (
                <div className={`message ${msg.type}`}>
                  <p className="message-text" role="alert">{msg.text}</p>
                </div>
              )}

              <form className="auth-form" onSubmit={onRegister} noValidate>
                <div className="form-field">
                  <label htmlFor="register-full-name">Full Name</label>
                  <input type="text" id="register-full-name" name="full_name" autoComplete="name" required placeholder="Your full name" />
                </div>

                <div className="form-field">
                  <label htmlFor="register-email">Email</label>
                  <div className="input-with-icon">
                    <span className="input-icon material-symbols-outlined" aria-hidden="true">mail</span>
                    <input type="email" id="register-email" name="email" autoComplete="email" required placeholder="you@company.com" />
                  </div>
                </div>

                <div className="form-field">
                  <label htmlFor="register-phone">Phone Number</label>
                  <div className="input-with-icon">
                    <span className="input-icon material-symbols-outlined" aria-hidden="true">call</span>
                    <input type="tel" id="register-phone" name="phone" autoComplete="tel" placeholder="+254 700 000 000" />
                  </div>
                </div>

                <div className="form-field">
                  <label>I am a</label>
                  <div className="role-options">
                    <label className="role-option">
                      <input type="radio" name="role" value="client" />
                      <span>Client</span>
                    </label>
                    <label className="role-option">
                      <input type="radio" name="role" value="site_agent" />
                      <span>Site Agent</span>
                    </label>
                    <label className="role-option">
                      <input type="radio" name="role" value="project_manager" />
                      <span>Project Manager</span>
                    </label>
                  </div>
                </div>

                <div className="form-grid">
                  <PasswordField id="register-password" name="password" autoComplete="new-password" label="Password" placeholder="At least 6 characters" minLength={6} required />
                  <PasswordField id="register-confirm-password" name="confirm_password" autoComplete="new-password" label="Confirm Password" placeholder="Re-enter your password" required />
                </div>

                <button type="submit" id="register-button" className="btn btn-primary btn-block" disabled={busy}>
                  <span className="material-symbols-outlined" aria-hidden="true">person_add</span>
                  <span>Create Account</span>
                </button>
              </form>

              <div className="auth-links" style={{ justifyContent: 'center', marginTop: 16 }}>
                <a href="#signin" style={{ fontWeight: 600 }} onClick={(e) => { e.preventDefault(); go('signin'); }}>
                  Already have an account? <strong>Sign in</strong>
                </a>
              </div>
              {FOOTER}
            </div>

            {/* Forgot Password */}
            <div className={`auth-state${state === 'forgot' ? ' is-active' : ''}`}>
              <header className="auth-header">
                <h2 className="auth-title">Reset your password</h2>
                <p className="auth-subtitle">Enter your email and we will send you a reset link.</p>
              </header>

              {msg.text && state === 'forgot' && (
                <div className={`message ${msg.type}`}>
                  <p className="message-text" role="alert">{msg.text}</p>
                </div>
              )}

              <form className="auth-form" onSubmit={onForgot} noValidate>
                <div className="form-field">
                  <label htmlFor="forgot-email">Email</label>
                  <input type="email" id="forgot-email" name="email" autoComplete="email" required placeholder="you@company.com" />
                </div>
                <button type="submit" id="forgot-button" className="btn btn-primary btn-block" disabled={busy}>
                  SEND RESET LINK
                </button>
              </form>

              <div className="auth-divider"><span>Remember your password?</span></div>
              <button type="button" className="btn btn-secondary btn-block" onClick={() => go('signin')}>
                Back to sign in
              </button>
              {FOOTER}
            </div>
          </div>
        </div>
      </main>

      {/* ══════════════════════════════════════════════════════════════════
          The story: one page, in the same voice as the hero. A visitor who
          reads to the end understands the problem, the parties, the record
          and the way in.
          ══════════════════════════════════════════════════════════════════ */}
      <div className="vm-story">

        {/* ── 1. The problem ──────────────────────────────────────────── */}
        <section className="vm-band vm-band-grid vm-band-problem">
          <div className="vm-inner">
            <p className="vm-kicker">The problem</p>
            <h2 className="vm-h2">Progress needs records, not just reassurance.</h2>
            <p className="vm-lead">
              You fund the project, but you are rarely on site. Every remote client runs
              into the same five problems: visibility, proof of progress, money,
              records and trust.
            </p>

            <div className="vm-problem">
              <ol className="vm-frictions">
                <li>
                  <span className="vm-friction-num">1</span>
                  <div>
                    <strong>You are not there.</strong>
                    <span>Statements like &ldquo;the project is 60% complete&rdquo; arrive without a record behind them.</span>
                  </div>
                </li>
                <li>
                  <span className="vm-friction-num">2</span>
                  <div>
                    <strong>Progress is a claim.</strong>
                    <span>&ldquo;The foundation is complete&rdquo; with nothing to check it against.</span>
                  </div>
                </li>
                <li>
                  <span className="vm-friction-num">3</span>
                  <div>
                    <strong>Evidence is scattered.</strong>
                    <span>Receipts, photos and updates sit across chats, spreadsheets and email.</span>
                  </div>
                </li>
                <li>
                  <span className="vm-friction-num">4</span>
                  <div>
                    <strong>Money has no shared ledger.</strong>
                    <span>No one place both sides can point to and agree on.</span>
                  </div>
                </li>
                <li>
                  <span className="vm-friction-num">5</span>
                  <div>
                    <strong>Trust has to be taken on faith.</strong>
                    <span>Honesty is requested, not demonstrated, until you are standing on the site.</span>
                  </div>
                </li>
              </ol>

              <figure className="vm-gap" aria-hidden="true">
                <div className="vm-gap-lane">
                  <p className="vm-gap-label">What was actually done on site</p>
                  <div className="vm-gap-tokens">
                    <span /><span /><span /><span /><span /><span />
                    <span /><span /><span /><span /><span /><span />
                  </div>
                </div>
                <div className="vm-gap-arrow">
                  <span className="ms" aria-hidden="true">south</span>
                  <span>what reaches you</span>
                </div>
                <div className="vm-gap-lane">
                  <p className="vm-gap-label">What you can show later</p>
                  <div className="vm-gap-tokens">
                    <span /><span className="vm-token-ghost" />
                  </div>
                </div>
                <figcaption className="vm-gap-caption">
                  The daily facts exist. Your copy is thin: a phone call, a photo in a
                  chat, an estimate from memory. For the diaspora client this is a
                  trust issue before it is a convenience issue.
                </figcaption>
              </figure>
            </div>
          </div>
        </section>

        {/* ── 2. Distance: the globe ──────────────────────────────────── */}
        <section className="vm-band vm-band-alt" id="distance">
          <div className="vm-inner">
            <p className="vm-kicker">One promise</p>
            <h2 className="vm-h2">Wherever you are, the construction site is brought to&nbsp;you.</h2>
            <p className="vm-lead">
              Clients fund Kenyan projects from London, Toronto, Houston, Dubai,
              Johannesburg and Sydney. And from Mombasa and Kisumu, Kenyans build
              in a county they cannot watch every day. Same problem, same answer:
              the site comes to you, verified daily.
            </p>

            <div className="vm-globe-wrap">
              <canvas
                id="globeCanvas"
                className="vm-globe-canvas"
                aria-label="Slowly rotating dotted globe of the world; wherever a client is, the construction site is brought to them, verified daily from Kenya"
              />
            </div>
          </div>
        </section>

        {/* ── 3. One record, verified ─────────────────────────────────── */}
        <section className="vm-band vm-band-white" id="platform">
          <div className="vm-inner">
            <p className="vm-kicker">The platform</p>
            <h2 className="vm-h2">One record, written once. Verified before anyone else sees&nbsp;it.</h2>
            <p className="vm-lead">
              Project progress, site activity, photographs, records and money come
              together in one secure place. The site agent writes; the project manager
              reviews every submission; you receive the checked result only.
            </p>

            <div className="vm-gate">
              <div className="vm-gate-step">
                <span className="vm-gate-icon ms" aria-hidden="true">edit_note</span>
                <h3>The site agent writes</h3>
                <p>
                  Reports and registers from the field, on the day they happen: labour,
                  deliveries, materials, plant and equipment, quality checks, photos
                  and expenses.
                </p>
              </div>
              <span className="vm-gate-arrow ms" aria-hidden="true">east</span>
              <div className="vm-gate-step vm-gate-step-review">
                <span className="vm-gate-icon ms" aria-hidden="true">fact_check</span>
                <h3>The project manager verifies</h3>
                <p>
                  Every submission is reviewed, one by one. Approve, or reject with a
                  reason that goes back to the agent to correct.
                </p>
              </div>
              <span className="vm-gate-arrow ms" aria-hidden="true">east</span>
              <div className="vm-gate-step">
                <span className="vm-gate-icon ms" aria-hidden="true">verified</span>
                <h3>You receive the record</h3>
                <p>
                  Your workspace shows the approved version only, in one place you can
                  reopen any day.
                </p>
              </div>
            </div>

            <div className="vm-roles">
              <div className="vm-role">
                <header className="vm-role-head">
                  <span className="vm-role-icon ms" aria-hidden="true">construction</span>
                  <div>
                    <h4>Site Agent</h4>
                    <p>In the field, every day</p>
                  </div>
                </header>
                <ul className="vm-role-points">
                  <li><span className="ms" aria-hidden="true">check_circle</span>Writes the daily report: weather, stage, people, progress</li>
                  <li><span className="ms" aria-hidden="true">check_circle</span>Logs labour, deliveries, materials, plant and quality</li>
                  <li><span className="ms" aria-hidden="true">check_circle</span>Captures photos and documents with time and GPS</li>
                  <li><span className="ms" aria-hidden="true">check_circle</span>Submits the day&rsquo;s work for review, and it locks</li>
                </ul>
              </div>

              <div className="vm-role vm-role-manager">
                <header className="vm-role-head">
                  <span className="vm-role-icon ms" aria-hidden="true">engineering</span>
                  <div>
                    <h4>Project Manager</h4>
                    <p>Your main link to the site</p>
                  </div>
                </header>
                <ul className="vm-role-points">
                  <li><span className="ms" aria-hidden="true">check_circle</span>Reviews every submission before you see it</li>
                  <li><span className="ms" aria-hidden="true">check_circle</span>Sets up the project: your budget, stages and milestones</li>
                  <li><span className="ms" aria-hidden="true">check_circle</span>Approves or rejects the site agent&rsquo;s submissions, with recorded reasons</li>
                  <li><span className="ms" aria-hidden="true">check_circle</span>Keeps spending measured against your budget</li>
                </ul>
              </div>

              <div className="vm-role">
                <header className="vm-role-head">
                  <span className="vm-role-icon ms" aria-hidden="true">person</span>
                  <div>
                    <h4>You, the Client</h4>
                    <p>Owns and funds the work</p>
                  </div>
                </header>
                <ul className="vm-role-points">
                  <li><span className="ms" aria-hidden="true">check_circle</span>Requests the project and follows its activation</li>
                  <li><span className="ms" aria-hidden="true">check_circle</span>Reads approved reports, verified photos and documents</li>
                  <li><span className="ms" aria-hidden="true">check_circle</span>Gets an alert when new work is approved</li>
                  <li><span className="ms" aria-hidden="true">check_circle</span>Downloads formal reports for bank, board or co-owners</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* ── 4. The daily report: the heartbeat ──────────────────────── */}
        <section className="vm-band" id="daily-report">
          <div className="vm-inner">
            <p className="vm-kicker">The daily report</p>
            <h2 className="vm-h2">Every day on site, a report of that day.</h2>
            <p className="vm-lead">
              This is the heart of Site Control 360. Each working day, your site
              agent writes the day down from the field: the stage, the weather, the
              people on site, how far the work has come and what happens next.
              Before any of it reaches you, your project manager verifies it.
            </p>

            <div className="vm-daily">
              <div className="vm-daily-stack">
                <div className="vm-daily-card">
                  <div className="vm-daily-head">
                    <span className="ms" aria-hidden="true">description</span>
                    <strong>Daily Site Report</strong>
                    <span className="vm-daily-date">12 Mar 2026</span>
                    <span className="vm-badge vm-badge-green">Verified</span>
                  </div>
                  <div className="vm-daily-meta">
                    <span><span className="ms" aria-hidden="true">apartment</span>Riverside Apartments · Westlands, Nairobi</span>
                    <span><span className="ms" aria-hidden="true">engineering</span>Written by A. Otieno, Site Agent</span>
                  </div>
                  <div className="vm-daily-fields">
                    <div className="vm-daily-field"><span>Stage</span><strong>3 · Walling</strong></div>
                    <div className="vm-daily-field"><span>Weather</span><strong>Sunny · 27°C</strong></div>
                    <div className="vm-daily-field"><span>People on site</span><strong>14</strong></div>
                    <div className="vm-daily-field"><span>Progress</span><strong>42%</strong></div>
                  </div>
                  <div className="vm-daily-work">
                    <span>Work completed today</span>
                    <ul>
                      <li>East wing blockwork to ring beam level, courses 1 to 12.</li>
                      <li>Stair core concrete pour completed by 4:00 pm; six cube samples cast for testing.</li>
                      <li>West column reinforcement fixed and ready for inspection.</li>
                    </ul>
                  </div>
                  <div className="vm-daily-grid">
                    <div className="vm-daily-cell">
                      <span>Labour on site</span>
                      <p>1 foreman, 6 masons, 4 labourers, 2 steel fixers, 1 welder</p>
                    </div>
                    <div className="vm-daily-cell">
                      <span>Materials used</span>
                      <p>40 bags cement, 1.2 t reinforcement steel, 8 m³ ballast, 320 machine-cut stones</p>
                    </div>
                    <div className="vm-daily-cell">
                      <span>Plant on site</span>
                      <p>Concrete mixer, poker vibrator, plate compactor, scaffold sets</p>
                    </div>
                    <div className="vm-daily-cell">
                      <span>Quality &amp; safety</span>
                      <p>Cube samples cast for the 7-day test. PPE checked at the gate. No incidents.</p>
                    </div>
                  </div>
                  <div className="vm-daily-remarks">
                    <span>Issues &amp; next steps</span>
                    <p>
                      No delays today. Plaster sand delivery expected tomorrow at 9 am. Next:
                      west ring beam formwork and electrical conduits in the east wing.
                    </p>
                  </div>
                  <div className="vm-daily-foot">
                    <span className="vm-chip-ok"><span className="ms" aria-hidden="true">photo_camera</span>3 photos attached</span>
                    <span className="vm-chip-ok"><span className="ms" aria-hidden="true">verified</span>Captured 6:48 am · Verified 11:40 am</span>
                  </div>
                </div>
                <div className="vm-daily-card vm-daily-muted">
                  <div className="vm-daily-head">
                    <span className="ms" aria-hidden="true">description</span>
                    <strong>Daily Site Report</strong>
                    <span className="vm-daily-date">11 Mar 2026</span>
                    <span className="vm-badge vm-badge-green">Verified</span>
                  </div>
                  <div className="vm-daily-fields">
                    <div className="vm-daily-field"><span>Stage</span><strong>3 · Walling</strong></div>
                    <div className="vm-daily-field"><span>Weather</span><strong>Cloudy</strong></div>
                    <div className="vm-daily-field"><span>People on site</span><strong>12</strong></div>
                    <div className="vm-daily-field"><span>Progress</span><strong>41%</strong></div>
                  </div>
                </div>
              </div>

              <ol className="vm-daily-steps">
                <li>
                  <span className="ms" aria-hidden="true">schedule</span>
                  <div>
                    <strong>Captured on site</strong>
                    <span>Written by the site agent that day, with time and GPS attached.</span>
                  </div>
                </li>
                <li>
                  <span className="ms" aria-hidden="true">fact_check</span>
                  <div>
                    <strong>Submitted for review</strong>
                    <span>The report locks on submission. Your manager approves it, or rejects it with a reason.</span>
                  </div>
                </li>
                <li>
                  <span className="ms" aria-hidden="true">notifications</span>
                  <div>
                    <strong>You are notified</strong>
                    <span>An alert when new work is approved, wherever in the world you are.</span>
                  </div>
                </li>
              </ol>
            </div>
          </div>
        </section>

        {/* ── 5. All these features, one organized dashboard ──────────── */}
        <section className="vm-band vm-band-white" id="dashboard">
          <div className="vm-inner">
            <p className="vm-kicker">Your view</p>
            <h2 className="vm-h2">All these features. One organized dashboard.</h2>
            <p className="vm-lead">
              Daily reports, photos, documents, progress, timeline, materials,
              labour, plant and equipment, quality checks and finances: every
              feature and every report lives in one organized dashboard, instead of
              twelve scattered chats.
            </p>

            <div className="vm-appwin" aria-hidden="true">
              <aside className="vm-appside">
                <div className="vm-appside-brand">
                  <img src="/logo-white.png" alt="" />
                  <span>Site Control 360</span>
                </div>
                <nav className="vm-appside-nav">
                  <span className="vm-appside-link vm-appside-back"><span className="ms">arrow_back</span>My Projects</span>
                  <span className="vm-appside-link is-active"><span className="ms">home</span>Overview</span>
                  <span className="vm-appside-link"><span className="ms">article</span>Daily Reports</span>
                  <span className="vm-appside-link"><span className="ms">account_balance</span>Finances</span>
                  <span className="vm-appside-link"><span className="ms">photo_camera</span>Photos</span>
                  <span className="vm-appside-link"><span className="ms">folder_open</span>Documents</span>
                  <span className="vm-appside-link"><span className="ms">engineering</span>Progress Tracker</span>
                  <span className="vm-appside-link"><span className="ms">history</span>Timeline</span>
                  <span className="vm-appside-link"><span className="ms">inventory_2</span>Materials</span>
                  <span className="vm-appside-link"><span className="ms">groups</span>Labour</span>
                  <span className="vm-appside-link"><span className="ms">construction</span>Plant &amp; Equipment</span>
                  <span className="vm-appside-link"><span className="ms">fact_check</span>Quality Verification</span>
                </nav>
                <div className="vm-appside-foot">
                  <span className="vm-appside-avatar">RP</span>
                  <div>
                    <strong>R. Pelley</strong>
                    <span>Client</span>
                  </div>
                </div>
              </aside>

              <div className="vm-appmain">
                <div className="vm-apptopbar">
                  <span data-typed="Good morning, R. Pelley">Good morning, R. Pelley</span>
                  <span className="vm-apptopbar-right">
                    Thu, 12 Mar 2026
                    <span className="vm-badge vm-badge-green">Active</span>
                  </span>
                </div>

                <div className="vm-appcontent">
                  <p className="vm-appcrumb">
                    My Projects <span className="ms">chevron_right</span> Riverside Apartments
                    <span className="ms">chevron_right</span> Overview
                  </p>
                  <div className="vm-apphead">
                    <h5>Overview</h5>
                  </div>

                  <div className="vm-appintro">
                    <h6>Riverside Apartments</h6>
                    <p>
                      Follow the work as it happens: verified site updates, labour,
                      materials and progress records.
                    </p>
                    <div className="vm-appprog">
                      <span className="vm-appprog-label">
                        Overall Progress
                        <b className="vm-appprog-value"><span data-count="42">42</span>%</b>
                      </span>
                      <div className="vm-appprog-bar"><span data-target="42" style={{ width: '42%' }} /></div>
                    </div>
                  </div>

                  <div className="vm-panel">
                    <h6><span className="ms">bolt</span>Site Activity</h6>
                    <div className="vm-appstats">
                      <div className="vm-appstat">
                        <span className="ms">article</span>
                        <p className="vm-appstat-label">Approved Updates</p>
                        <p className="vm-appstat-value"><span data-count="38">38</span></p>
                      </div>
                      <div className="vm-appstat">
                        <span className="ms">photo_camera</span>
                        <p className="vm-appstat-label">Site Photos</p>
                        <p className="vm-appstat-value"><span data-count="112">112</span></p>
                      </div>
                      <div className="vm-appstat">
                        <span className="ms">engineering</span>
                        <p className="vm-appstat-label">Stages Completed</p>
                        <p className="vm-appstat-value"><span data-count="1">1</span> of 6</p>
                      </div>
                    </div>
                  </div>

                  <div className="vm-panel">
                    <h6><span className="ms">visibility</span>Latest Site Activity</h6>

                    <div className="vm-appreport">
                      <div className="vm-appreport-head">
                        <strong>12 Mar 2026</strong>
                        <span className="vm-badge vm-badge-green">Approved</span>
                        <span className="vm-appreport-time">Submitted 6:48 am · Approved 11:40 am</span>
                      </div>
                      <div className="vm-appreport-meta">
                        <span><span className="ms">engineering</span>Stage: 3 · Walling, 1st floor</span>
                        <span><span className="ms">trending_up</span>Progress: 42%</span>
                        <span><span className="ms">wb_sunny</span>Weather: Sunny · 27°C</span>
                        <span><span className="ms">groups</span>Labour: 14 on site</span>
                      </div>
                      <p className="vm-appreport-body">
                        East wing blockwork to ring beam level completed and lined. Concrete
                        pour for the stair core (14 m³, grade 25) started at 9:10 am with the
                        pump on site and finished by 4:00 pm, vibrated in three lifts. Formwork
                        to the west stair soffit struck after the afternoon check; curing water
                        applied twice.
                      </p>
                      <div className="vm-applinks">
                        <span className="vm-applinkchip"><span className="ms">groups</span>Labour · 2 entries · 14 workers</span>
                        <span className="vm-applinkchip"><span className="ms">local_shipping</span>Deliveries · 1 verified</span>
                        <span className="vm-applinkchip"><span className="ms">precision_manufacturing</span>Plant · 2 logs</span>
                        <span className="vm-applinkchip"><span className="ms">fact_check</span>Quality · 2 checks passed</span>
                      </div>
                      <div className="vm-appphotos">
                        <figure className="vm-appphoto">
                          <div className="vm-appphoto-thumb">
                            <span className="ms">photo_camera</span>
                          </div>
                          <figcaption>
                            <strong>Stair core concrete pour, third lift</strong>
                            <span>1.2921°S · 36.8054°E · Verified</span>
                          </figcaption>
                        </figure>
                        <figure className="vm-appphoto">
                          <div className="vm-appphoto-thumb">
                            <span className="ms">photo_camera</span>
                          </div>
                          <figcaption>
                            <strong>East wing blockwork to ring beam</strong>
                            <span>1.2920°S · 36.8056°E · Verified</span>
                          </figcaption>
                        </figure>
                        <figure className="vm-appphoto">
                          <div className="vm-appphoto-thumb">
                            <span className="ms">photo_camera</span>
                          </div>
                          <figcaption>
                            <strong>Formwork struck, west stair soffit</strong>
                            <span>1.2918°S · 36.8051°E · Verified</span>
                          </figcaption>
                        </figure>
                      </div>
                      <div className="vm-appreport-foot">
                        <span className="vm-chip-ok"><span className="ms">verified</span>Verified by J. Mwangi · Project Manager</span>
                        <span className="vm-appreport-issue">Issues: none reported</span>
                      </div>
                    </div>
                  </div>

                  <div className="vm-appgrid">
                    <div className="vm-panel">
                      <h6><span className="ms">groups</span>Labour · 12 Mar</h6>
                      <table className="vm-apptable">
                        <thead><tr><th>Trade</th><th>Workers</th><th>Details</th></tr></thead>
                        <tbody>
                          <tr><td>Masons</td><td>6</td><td>East wing blockwork to ring beam</td></tr>
                          <tr><td>Steel fixers</td><td>3</td><td>Stair core reinforcement</td></tr>
                          <tr><td>General workers</td><td>5</td><td>Pour support and curing</td></tr>
                        </tbody>
                      </table>
                    </div>
                    <div className="vm-panel">
                      <h6><span className="ms">local_shipping</span>Materials delivered · 12 Mar</h6>
                      <table className="vm-apptable">
                        <thead><tr><th>Material</th><th>Quantity</th><th>Details</th></tr></thead>
                        <tbody>
                          <tr><td>Ready-mix concrete</td><td>14 m³</td><td>Stair core pour, grade 25</td></tr>
                          <tr><td>Cement</td><td>40 bags</td><td>Blockwork mortar</td></tr>
                          <tr><td>Rebar Y12</td><td>26 pcs</td><td>Stair landing steel</td></tr>
                        </tbody>
                      </table>
                    </div>
                    <div className="vm-panel">
                      <h6><span className="ms">precision_manufacturing</span>Plant &amp; equipment · 12 Mar</h6>
                      <table className="vm-apptable">
                        <thead><tr><th>Equipment</th><th>Hours</th><th>Details</th></tr></thead>
                        <tbody>
                          <tr><td>Concrete pump</td><td>7 h</td><td>Stair core pour</td></tr>
                          <tr><td>Vibrating poker</td><td>6 h</td><td>Compaction, three lifts</td></tr>
                        </tbody>
                      </table>
                    </div>
                    <div className="vm-panel">
                      <h6><span className="ms">fact_check</span>Quality checks · 12 Mar</h6>
                      <table className="vm-apptable">
                        <thead><tr><th>Item</th><th>Result</th><th>Remarks</th></tr></thead>
                        <tbody>
                          <tr>
                            <td>Slump test, pour 1</td>
                            <td><span className="vm-badge vm-badge-green">Pass</span></td>
                            <td>80 mm, within limits</td>
                          </tr>
                          <tr>
                            <td>Blockwork line and level</td>
                            <td><span className="vm-badge vm-badge-green">Pass</span></td>
                            <td>Checked at ring beam</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── 6. Evidence ─────────────────────────────────────────────── */}
        <section className="vm-band">
          <div className="vm-inner">
            <p className="vm-kicker">The evidence</p>
            <h2 className="vm-h2">Every photo becomes evidence you can check.</h2>
            <p className="vm-lead">
              From &ldquo;here are some pictures&rdquo; to verified photographic
              evidence of a specific activity, recorded on a specific date.
            </p>

            <div className="vm-flow">
              <div className="vm-flow-chip">
                <span className="ms" aria-hidden="true">photo_camera</span>
                <strong>1. Capture on site</strong>
                <span>Date, time and GPS position, added automatically at capture.</span>
              </div>
              <span className="vm-flow-arrow ms" aria-hidden="true">east</span>
              <div className="vm-flow-chip">
                <span className="ms" aria-hidden="true">sell</span>
                <strong>2. Sort &amp; tag</strong>
                <span>Work Progress, Materials, Equipment, Labour, Safety, Issues.</span>
              </div>
              <span className="vm-flow-arrow ms" aria-hidden="true">east</span>
              <div className="vm-flow-chip">
                <span className="ms" aria-hidden="true">badge</span>
                <strong>3. Caption &amp; upload</strong>
                <span>&ldquo;Second floor columns struck.&rdquo; Stored as Pending.</span>
              </div>
              <span className="vm-flow-arrow ms" aria-hidden="true">east</span>
              <div className="vm-flow-chip vm-flow-chip-gate">
                <span className="ms" aria-hidden="true">verified</span>
                <strong>4. Verified</strong>
                <span>Your manager checks each photo before it reaches you.</span>
              </div>
            </div>
          </div>
        </section>

        {/* ── 7. Reports ──────────────────────────────────────────────── */}
        <section className="vm-band vm-band-white">
          <div className="vm-inner vm-inner-narrow">
            <p className="vm-kicker">The reports</p>
            <h2 className="vm-h2">Records to read. Reports to print.</h2>
            <p className="vm-lead">
              The record is digital, but it does not have to stay on the screen.
              A printed report is another view of the same verified record: no
              reconstructing, no retelling. Choose a type and a date range, press
              Generate Report, and hand over the facts, not photos of a torn
              notebook. For the bank, the board or your co-owners: the evidence
              is already on file.
            </p>

            <div className="vm-builders">
              <div className="vm-builder"><span className="ms" aria-hidden="true">summarize</span><div><strong>Project Summary</strong><span>Project information, team and site activity summary.</span></div></div>
              <div className="vm-builder"><span className="ms" aria-hidden="true">trending_up</span><div><strong>Progress Report</strong><span>Stages, milestones and completion percentage.</span></div></div>
              <div className="vm-builder"><span className="ms" aria-hidden="true">photo_camera</span><div><strong>Site Update Report</strong><span>Approved updates, verified photos and remarks.</span></div></div>
              <div className="vm-builder"><span className="ms" aria-hidden="true">engineering</span><div><strong>Labour Report</strong><span>Who worked, in what trade, for how long.</span></div></div>
              <div className="vm-builder"><span className="ms" aria-hidden="true">inventory_2</span><div><strong>Materials Report</strong><span>Verified deliveries and material usage.</span></div></div>
            </div>

            <div className="vm-actions" aria-hidden="true">
              <span className="vm-action-btn vm-action-btn-primary">Generate Report</span>
              <span className="vm-action-btn">Print View</span>
              <span className="vm-action-btn">Download PDF</span>
              <span className="vm-action-btn">Download CSV</span>
            </div>
          </div>
        </section>

        {/* ── 8. Money: one of eleven sections ────────────────────────── */}
        <section className="vm-band">
          <div className="vm-inner">
            <p className="vm-kicker">Finances, verified</p>
            <h2 className="vm-h2">The money, one more part of the record.</h2>
            <p className="vm-lead">
              Spending sits on the same dashboard as the reports and photos it
              belongs with: submitted as pending, verified by your project manager,
              measured against the budget you agreed. Money is not the headline of
              Site Control 360: it is one of the eleven sections, held to the same
              standard as the rest.
            </p>

            <div className="vm-money vm-money-compact">
              <div className="vm-money-stat">
                <p className="vm-money-label">Approved Budget</p>
                <p className="vm-money-value">{'KSh\u00A042,500,000'}</p>
                <p className="vm-money-sub">The one contract sum, agreed at setup.</p>
              </div>
              <div className="vm-money-stat">
                <p className="vm-money-label">Approved Expenses</p>
                <p className="vm-money-value">{'KSh\u00A011,250,000'}</p>
                <p className="vm-money-sub">Only reviewed expenses count as spending.</p>
              </div>
              <div className="vm-money-stat">
                <p className="vm-money-label">Remaining Allocation</p>
                <p className="vm-money-value">{'KSh\u00A031,250,000'}</p>
                <p className="vm-money-sub">Budget minus approved spending.</p>
              </div>
            </div>

            <div className="vm-rule-chips">
              <span><strong>1</strong>Every expense starts pending</span>
              <span><strong>2</strong>Only approved spending counts</span>
              <span><strong>3</strong>Rejected stays out of every figure</span>
            </div>
          </div>
        </section>

        {/* ── 9. Getting started ──────────────────────────────────────── */}
        <section className="vm-band vm-band-white" id="get-started">
          <div className="vm-inner">
            <p className="vm-kicker">Get started</p>
            <h2 className="vm-h2">From sign-up to an active project.</h2>
            <p className="vm-lead">
              Create your account, submit your project request, and Valdon takes it
              from there: discussion and agreement first, then setup and activation.
              The paths differ by design, and that difference protects you.
            </p>

            <div className="vm-start-steps">
              <div className="vm-step-card">
                <span className="vm-step-num">01</span>
                <h4>Create your account</h4>
                <p>Clients start straight away. Site agents and project managers join only once Valdon approves them: they write the records you rely on, so nobody self-approves into a writing seat.</p>
              </div>
              <div className="vm-step-card">
                <span className="vm-step-num">02</span>
                <h4>Submit the request</h4>
                <p>Tell Valdon about the build you are planning: your drawings, the site and the schedule you have in mind. Choose Valdon&rsquo;s project manager or bring your own.</p>
              </div>
              <div className="vm-step-card">
                <span className="vm-step-num">03</span>
                <h4>Discussion &amp; agreement</h4>
                <p>With the Valdon team, by WhatsApp or phone, at your pace. You agree together on the scope, the budget and the milestones before any work begins.</p>
              </div>
              <div className="vm-step-card">
                <span className="vm-step-num">04</span>
                <h4>Valdon sets up the project</h4>
                <p>Your project manager prepares the plan behind the scenes: the budget, the stages, the team. Once setup is reviewed, the project activates and the full workspace opens.</p>
              </div>
            </div>
            <div className="vm-track">
              <span className="vm-track-chip is-done">Awaiting Onboarding</span>
              <span className="ms" aria-hidden="true">east</span>
              <span className="vm-track-chip is-done">Setup In Progress</span>
              <span className="ms" aria-hidden="true">east</span>
              <span className="vm-track-chip">Pending Admin Review</span>
              <span className="ms" aria-hidden="true">east</span>
              <span className="vm-track-chip vm-track-chip-final">Active</span>
            </div>
            <p className="vm-caption">
              Setup cannot be submitted half built: a budget, at least one site agent,
              at least one stage and one milestone are required. By activation, your
              record is complete.
            </p>
          </div>
        </section>

        {/* ── 10. Who it is for + honest limits ───────────────────────── */}
        <section className="vm-band">
          <div className="vm-inner">
            <p className="vm-kicker">Built for</p>
            <h2 className="vm-h2">You don&rsquo;t have to be there.</h2>
            <p className="vm-lead">
              Whether you are in Texas, London, Toronto, Nairobi, Mombasa or
              Kisumu, Site Control 360 gives you a digital window into your
              construction project.
            </p>

            <div className="vm-audience">
              <div className="vm-audience-card">
                <h4>Diaspora Clients &amp; Remote Owners</h4>
                <p>
                  Funding a project from abroad? Get a digital window into your site:
                  verified progress, photos and finances, without stepping on site.
                </p>
              </div>
              <div className="vm-audience-card">
                <h4>Kenyans Building Away From Home</h4>
                <p>
                  Living in Mombasa with your project in Kisumu, or in Nairobi funding
                  a build upcountry: the same window, the same verified record. You
                  do not have to travel to trust the work.
                </p>
              </div>
              <div className="vm-audience-card">
                <h4>Property Developers &amp; Investors</h4>
                <p>
                  Monitor multiple projects from one dashboard. Approved records,
                  financial transparency and alerts keep you in control across borders.
                </p>
              </div>
              <div className="vm-audience-card">
                <h4>Homeowners &amp; Institutions</h4>
                <p>
                  Churches, nonprofits and homeowners building their dream. Clear,
                  verified records you can show to banks, boards and co-owners.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── 11. FAQ ─────────────────────────────────────────────────── */}
        <section className="vm-band vm-band-white" id="faq">
          <div className="vm-inner vm-inner-narrow">
            <p className="vm-kicker">Questions?</p>
            <h2 className="vm-h2">Frequently asked questions</h2>
            <div className="vm-faq">
              <div className="vm-faq-item">
                <button className="vm-faq-button" onClick={() => toggleFaq(0)} aria-expanded={openFaq === 0}>
                  <span>What does Valdon Site Control 360 actually do?</span>
                  <span className="vm-faq-toggle ms" aria-hidden="true">{openFaq === 0 ? 'remove' : 'add'}</span>
                </button>
                {openFaq === 0 && (
                  <div className="vm-faq-answer">
                    <p>It brings project progress, financial information, site activity, photographs, inspections, approvals, documents and verified construction records into one secure platform. Your site agent writes the daily record, your project manager verifies it, and you see only the approved result.</p>
                  </div>
                )}
              </div>
              <div className="vm-faq-item">
                <button className="vm-faq-button" onClick={() => toggleFaq(1)} aria-expanded={openFaq === 1}>
                  <span>Who is this for?</span>
                  <span className="vm-faq-toggle ms" aria-hidden="true">{openFaq === 1 ? 'remove' : 'add'}</span>
                </button>
                {openFaq === 1 && (
                  <div className="vm-faq-answer">
                    <p>Diaspora clients, remote investors, property owners, developers and organizations managing construction from afar. If you cannot be at the site, Site Control 360 brings the site to you.</p>
                  </div>
                )}
              </div>
              <div className="vm-faq-item">
                <button className="vm-faq-button" onClick={() => toggleFaq(2)} aria-expanded={openFaq === 2}>
                  <span>What do the Site Agent and Project Manager do?</span>
                  <span className="vm-faq-toggle ms" aria-hidden="true">{openFaq === 2 ? 'remove' : 'add'}</span>
                </button>
                {openFaq === 2 && (
                  <div className="vm-faq-answer">
                    <p>The Site Agent records work from the field: daily reports, labour, deliveries, photos. The Project Manager reviews every submission and approves or rejects it. You, the client, only see what has been approved.</p>
                  </div>
                )}
              </div>
              <div className="vm-faq-item">
                <button className="vm-faq-button" onClick={() => toggleFaq(3)} aria-expanded={openFaq === 3}>
                  <span>Can I see pending or rejected records?</span>
                  <span className="vm-faq-toggle ms" aria-hidden="true">{openFaq === 3 ? 'remove' : 'add'}</span>
                </button>
                {openFaq === 3 && (
                  <div className="vm-faq-answer">
                    <p>No. Pending, draft and rejected records never appear in client views. Rejection is silent to you: the agent receives the reason and corrects it. You only see Approved or Verified records.</p>
                  </div>
                )}
              </div>
              <div className="vm-faq-item">
                <button className="vm-faq-button" onClick={() => toggleFaq(4)} aria-expanded={openFaq === 4}>
                  <span>Is this a guarantee that the work was done correctly?</span>
                  <span className="vm-faq-toggle ms" aria-hidden="true">{openFaq === 4 ? 'remove' : 'add'}</span>
                </button>
                {openFaq === 4 && (
                  <div className="vm-faq-answer">
                    <p>No. It is trust infrastructure: it makes site activity checkable. It does not eliminate fraud, does not replace physical inspection or professional judgment, and does not guarantee financial accuracy.</p>
                  </div>
                )}
              </div>
              <div className="vm-faq-item">
                <button className="vm-faq-button" onClick={() => toggleFaq(5)} aria-expanded={openFaq === 5}>
                  <span>How do I get started?</span>
                  <span className="vm-faq-toggle ms" aria-hidden="true">{openFaq === 5 ? 'remove' : 'add'}</span>
                </button>
                {openFaq === 5 && (
                  <div className="vm-faq-answer">
                    <p>Create a client account, submit a project request with your drawings, then discuss and agree the plan with Valdon. Once Valdon has set up and activated your project, the full workspace opens and you follow verified progress.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* ── Close ───────────────────────────────────────────────────── */}
        <section className="vm-cta">
          <div className="vm-inner">
            <h2 className="vm-cta-title">You don&rsquo;t have to be there<br />to know what is happening.</h2>
            <div className="vm-cta-actions">
              <a
                className="vm-cta-primary"
                href="#get-started"
                onClick={(e) => { e.preventDefault(); go('register'); }}
              >
                Create Account
              </a>
              <a
                className="vm-cta-ghost"
                href="#dashboard"
                onClick={(e) => { e.preventDefault(); scrollToSection('dashboard'); }}
              >
                See the Dashboard
              </a>
            </div>
            <p className="vm-cta-foot">
              Questions first? WhatsApp Valdon at
              {' '}<a href="https://wa.me/254706319209" target="_blank" rel="noreferrer">+254 706 319 209</a>.
            </p>
          </div>
        </section>

        <footer className="vm-foot">
          <p>
            &copy; {YEAR} Valdon Building &amp; Construction Services · Site Control 360 · Version 1.2
          </p>
        </footer>
      </div>
    </div>
  );
}

/** Smooth scroll to an element by id, honest about reduced motion. */
function scrollToSection(id) {
  const el = document.getElementById(id);
  if (el) scrollToPosition(el.getBoundingClientRect().top + window.scrollY - 24);
}

/**
 * Scroll to a position, smoothly where the browser can.
 *
 * Some environments (headless browsers, reduced-motion setups) quietly
 * ignore the smooth request, which would leave the form off screen after a
 * click; if the page has not moved by the time the animation would have
 * started, go there directly instead.
 */
function scrollToPosition(top) {
  const start = window.scrollY;
  const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  if (reduced) {
    window.scrollTo(0, top);
    return;
  }
  window.scrollTo({ top, behavior: 'smooth' });
  window.setTimeout(() => {
    if (Math.abs(window.scrollY - start) < 1 && Math.abs(top - start) > 2) {
      window.scrollTo(0, top);
    }
  }, 350);
}

/** Password input with the show/hide toggle from the original markup. */
function PasswordField({ id, name, label, autoComplete, placeholder, required, minLength }) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="form-field">
      <label htmlFor={id}>{label}</label>
      <div className="input-with-icon">
        <input
          type={visible ? 'text' : 'password'}
          id={id}
          name={name}
          autoComplete={autoComplete}
          placeholder={placeholder}
          required={required}
          minLength={minLength}
        />
        <button
          type="button"
          className="input-icon-toggle"
          aria-label={visible ? 'Hide password' : 'Show password'}
          onClick={() => setVisible((v) => !v)}
        >
          <span className="material-symbols-outlined" aria-hidden="true">
            {visible ? 'visibility_off' : 'visibility'}
          </span>
        </button>
      </div>
    </div>
  );
}
